import React, { useState } from 'react';
import { Download, FileSpreadsheet } from 'lucide-react';
import { exportService } from '@/services/export.service';
import { toast } from 'react-hot-toast';
import { Button, buttonVariants } from '../ui/button';
import { VariantProps } from 'class-variance-authority';

export type ExportType =
  | 'loans'
  | 'pricing-results'
  | 'complete-report'
  | 'fair-value-report'
  | 'fair-value-report-with-commentary'
  | 'portfolio-summary-report'
  | 'portfolio-analysis';

interface ExcelExportButtonProps {
  exportType: ExportType;
  icon?: React.ReactNode;
  label?: string;
  filename?: string;
  className?: string;
  variant?: VariantProps<typeof buttonVariants>['variant'];
  size?: VariantProps<typeof buttonVariants>['size'];
  loanId?: string | number;
}

export const ExcelExportButton: React.FC<ExcelExportButtonProps> = ({
  exportType,
  icon,
  label,
  filename,
  className = '',
  variant = 'outline',
  size = 'md',
  loanId,
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const getDefaultLabel = () => {
    switch (exportType) {
      case 'loans':
        return 'Export Loans';
      case 'pricing-results':
        return 'Export Pricing Results';
      case 'complete-report':
        return 'Export Complete Report';
      case 'fair-value-report':
        return 'Export Fair Value Report';
      case 'fair-value-report-with-commentary':
        return 'Export Fair Value Report with Commentary';
      case 'portfolio-summary-report':
        return 'Export Portfolio Summary Report';
      case 'portfolio-analysis':
        return 'Export Portfolio Analysis';
      default:
        return 'Export to Excel';
    }
  };

  const getDefaultFilename = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    switch (exportType) {
      case 'loans':
        return `loans-export-${timestamp}.xlsx`;
      case 'pricing-results':
        return `pricing-results-${timestamp}.xlsx`;
      case 'complete-report':
        return `complete-report-${timestamp}.xlsx`;
      case 'fair-value-report':
        return `fair-value-report-${timestamp}.xlsx`;
      case 'fair-value-report-with-commentary':
        return `fair-value-report-with-commentary-${timestamp}.xlsx`;
      case 'portfolio-summary-report':
        return `portfolio-summary-report-${timestamp}.xlsx`;
      case 'portfolio-analysis':
        return `portfolio-analysis-${timestamp}.xlsx`;
      default:
        return `export-${timestamp}.xlsx`;
    }
  };

  const handleExport = async () => {
    setIsExporting(true);

    const getLoadingMessage = () => {
      switch (exportType) {
        case 'complete-report':
          return 'Generating complete report... This may take a few seconds.';
        case 'pricing-results':
          return 'Exporting pricing results... (Recommended - fastest export)';
        case 'portfolio-analysis':
          return 'Generating portfolio analysis...';
        case 'fair-value-report':
          return 'Generating fair value report... This may take some time.';
        case 'fair-value-report-with-commentary':
          return 'Generating fair value report with commentary... This may take some time.';
        case 'portfolio-summary-report':
          return 'Generating portfolio summary report...';
        default:
          return 'Exporting to Excel...';
      }
    };

    const toastId = toast.loading(getLoadingMessage(), { duration: Infinity });

    try {
      let blob: Blob;

      switch (exportType) {
        case 'loans':
          blob = await exportService.exportLoansToExcel({
            limit: 1000,
            include_properties: true,
          });
          break;
        case 'pricing-results':
          blob = await exportService.exportPricingResultsToExcel({
            include_risk_metrics: true,
            include_market_data: true,
          });
          break;
        case 'complete-report':
          blob = await exportService.exportCompleteReportToExcel({
            limit: 500,
          });
          break;
        case 'fair-value-report':
          blob = await exportService.exportFairValueReportToExcel(
            {
              loan_id: loanId,
              report_type: 'standard',
            }
          );
          break;
        case 'fair-value-report-with-commentary':
          blob = await exportService.exportFairValueReportToExcel(
            {
              loan_id: loanId,
              report_type: 'commentary',
            }
          );
          break;
        case 'portfolio-summary-report':
          blob = await exportService.exportPortfolioSummaryReportToExcel();
          break;
        case 'portfolio-analysis':
          blob = await exportService.exportPortfolioAnalysisToExcel();
          break;
        default:
          throw new Error('Invalid export type');
      }

      // Download the file
      exportService.downloadFile(blob, filename || getDefaultFilename());

      // Dismiss loading toast and show success
      toast.dismiss(toastId);
      toast.success('Export completed successfully!', { duration: 3000 });
    } catch (error) {
      console.error('Export error:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to export. Please try again.';
      toast.error(errorMessage, { id: toastId, duration: 5000 });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      onClick={handleExport}
      disabled={isExporting}
      variant={variant as VariantProps<typeof buttonVariants>['variant']}
      size={size as VariantProps<typeof buttonVariants>['size']}
      className={className}
      data-test-id={`export-${exportType}-button`}
    >
      {icon ? icon : <FileSpreadsheet className="h-4 w-4" />}
      <span>{label || getDefaultLabel()}</span>
      {isExporting ? (
        <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
      ) : (
        <Download className="h-4 w-4" />
      )}
    </Button>
  );
};
